'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Search, ChevronUp, ChevronDown, Trash2, Copy, BarChart2, CheckSquare, Send, Calendar, Zap, Clock, AlertCircle, CheckCircle2, TrendingDown, ShieldCheck, ShieldAlert, Loader2, ExternalLink, Filter, Users, Webhook, RefreshCw } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import CampaignForm from './CampaignForm';
import { campaignService, type Campaign, type CampaignStatus } from '@/lib/services/mailflowService';
import Icon from '@/components/ui/AppIcon';


const statusVariant: Record<CampaignStatus, 'success' | 'primary' | 'neutral' | 'warning' | 'info'> = {
  sent: 'success',
  scheduled: 'primary',
  draft: 'neutral',
  sending: 'warning',
  archived: 'info',
};

const statusFilters = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Sending', value: 'sending' },
  { label: 'Sent', value: 'sent' },
  { label: 'Archived', value: 'archived' },
];

type SendMode = 'immediate' | 'scheduled';
type SendStep = 'options' | 'confirm';

interface BounceStats {
  campaignId: number;
  campaignName: string;
  sent: number;
  delivered: number;
  hardBounces: number;
  softBounces: number;
  totalBounces: number;
  bounceRate: number;
  deliveryRate: number;
  openRate: number;
  ctr: number;
  unsubscriptions: number;
  spamReports: number;
  bounceStatus: 'ok' | 'warning' | 'critical';
  compliance: { bounceStatus: string; message: string };
}

interface WebhookStats {
  campaign_id: string;
  hasData: boolean;
  message?: string;
  delivered_count: number;
  bounce_count: number;
  hard_bounce_count: number;
  soft_bounce_count: number;
  open_count: number;
  click_count: number;
  unsubscribe_count: number;
  spam_count: number;
  bounce_rate: number;
  delivery_rate: number;
  open_rate: number;
  ctr: number;
  bounce_status: 'ok' | 'warning' | 'critical';
  compliance_flagged: boolean;
  compliance_message: string;
  last_event_at: string | null;
}

// ─── Bounce Stats Panel ───────────────────────────────────────────────────────

function BounceStatsPanel({ stats }: { stats: BounceStats }) {
  const statusColor = stats.bounceStatus === 'critical' ?'border-red-200 bg-red-50'
    : stats.bounceStatus === 'warning' ?'border-amber-200 bg-amber-50' :'border-green-200 bg-green-50';

  const msgColor = stats.bounceStatus === 'critical' ?'text-red-700'
    : stats.bounceStatus === 'warning' ?'text-amber-700' :'text-green-700';

  const Icon = stats.bounceStatus === 'ok' ? ShieldCheck : ShieldAlert;
  const iconColor = stats.bounceStatus === 'critical' ?'text-red-500'
    : stats.bounceStatus === 'warning' ?'text-amber-500' :'text-green-500';

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${statusColor}`}>
      <div className="flex items-start gap-2">
        <Icon size={16} className={`mt-0.5 flex-shrink-0 ${iconColor}`} />
        <p className={`text-xs font-medium ${msgColor}`}>{stats.compliance.message}</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Delivered', value: `${stats.deliveryRate}%` },
          { label: 'Bounced', value: `${stats.bounceRate}%` },
          { label: 'Open Rate', value: `${stats.openRate}%` },
          { label: 'Hard Bounces', value: stats.hardBounces.toLocaleString() },
          { label: 'Soft Bounces', value: stats.softBounces.toLocaleString() },
          { label: 'Unsubscribes', value: stats.unsubscriptions.toLocaleString() },
        ].map((item) => (
          <div key={item.label} className="bg-white/60 rounded-lg px-2 py-1.5 text-center">
            <p className="text-xs font-bold text-foreground">{item.value}</p>
            <p className="text-[10px] text-muted-foreground">{item.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Webhook Stats Panel ──────────────────────────────────────────────────────

function WebhookStatsPanel({ stats, onRefresh, loading }: { stats: WebhookStats; onRefresh: () => void; loading: boolean }) {
  if (!stats.hasData) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/20 p-4 flex items-center gap-3">
        <Webhook size={16} className="text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-foreground">Waiting for Brevo webhook events</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Real-time bounce, delivery, open &amp; click data will appear here as Brevo sends events.</p>
        </div>
        <button onClick={onRefresh} disabled={loading} className="flex items-center gap-1 text-xs text-primary font-semibold hover:underline disabled:opacity-50">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
    );
  }

  const statusColor = stats.bounce_status === 'critical' ?'border-red-200 bg-red-50'
    : stats.bounce_status === 'warning' ?'border-amber-200 bg-amber-50' :'border-green-200 bg-green-50';

  const msgColor = stats.bounce_status === 'critical' ? 'text-red-700'
    : stats.bounce_status === 'warning' ? 'text-amber-700' : 'text-green-700';

  const StatusIcon = stats.bounce_status === 'ok' ? ShieldCheck : ShieldAlert;
  const iconColor = stats.bounce_status === 'critical' ? 'text-red-500'
    : stats.bounce_status === 'warning' ? 'text-amber-500' : 'text-green-500';

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${statusColor}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <StatusIcon size={15} className={`mt-0.5 flex-shrink-0 ${iconColor}`} />
          <div className="min-w-0">
            <p className="text-xs font-bold text-foreground flex items-center gap-1.5">
              <Webhook size={11} className="text-muted-foreground" /> Real-time Webhook Stats
            </p>
            <p className={`text-xs font-medium mt-0.5 ${msgColor}`}>{stats.compliance_message}</p>
          </div>
        </div>
        <button onClick={onRefresh} disabled={loading} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 flex-shrink-0">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: 'Delivered', value: `${stats.delivery_rate}%`, sub: stats.delivered_count.toLocaleString() },
          { label: 'Bounced', value: `${stats.bounce_rate}%`, sub: `${stats.hard_bounce_count}H / ${stats.soft_bounce_count}S` },
          { label: 'Opens', value: `${stats.open_rate}%`, sub: stats.open_count.toLocaleString() },
          { label: 'Clicks', value: `${stats.ctr}%`, sub: stats.click_count.toLocaleString() },
        ].map((item) => (
          <div key={item.label} className="bg-white/60 rounded-lg px-2 py-2 text-center">
            <p className="text-xs font-bold text-foreground">{item.value}</p>
            <p className="text-[10px] text-muted-foreground">{item.label}</p>
            <p className="text-[9px] text-muted-foreground/70 mt-0.5">{item.sub}</p>
          </div>
        ))}
      </div>
      {stats.last_event_at && (
        <p className="text-[10px] text-muted-foreground text-right">
          Last event: {new Date(stats.last_event_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
        </p>
      )}
    </div>
  );
}

// ─── Segment types (local) ────────────────────────────────────────────────────

interface LocalSegment {
  id: string;
  name: string;
  subscriberCount: number;
  color: string;
}

const BUILT_IN_SEGMENTS: LocalSegment[] = [
  { id: 'all', name: 'All Subscribers', subscriberCount: 3175, color: '#5b35e8' },
  { id: 'seg-1', name: 'Active Engaged Users', subscriberCount: 1842, color: '#5b35e8' },
  { id: 'seg-2', name: 'Re-engagement Targets', subscriberCount: 634, color: '#ff5c2b' },
  { id: 'seg-3', name: 'VIP Tag Holders', subscriberCount: 287, color: '#f59e0b' },
  { id: 'seg-4', name: 'New Subscribers (30d)', subscriberCount: 412, color: '#10b981' },
];

// ─── Send Dialog ──────────────────────────────────────────────────────────────

interface SendDialogProps {
  campaign: Campaign | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (campaign: Campaign, mode: SendMode, scheduledAt?: string, segmentId?: string) => Promise<void>;
}

function SendDialog({ campaign, open, onClose, onConfirm }: SendDialogProps) {
  const [step, setStep] = useState<SendStep>('options');
  const [mode, setMode] = useState<SendMode>('immediate');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>('all');
  const [segmentExpanded, setSegmentExpanded] = useState(false);

  const selectedSegment = BUILT_IN_SEGMENTS.find((s) => s.id === selectedSegmentId) || BUILT_IN_SEGMENTS[0];

  useEffect(() => {
    if (open) {
      setStep('options');
      setMode('immediate');
      setScheduledDate('');
      setScheduledTime('');
      setSending(false);
      setSelectedSegmentId('all');
      setSegmentExpanded(false);
    }
  }, [open]);

  if (!campaign) return null;

  const minDate = new Date();
  minDate.setMinutes(minDate.getMinutes() + 5);
  const minDateStr = minDate.toISOString().slice(0, 10);
  const minTimeStr = minDate.toISOString().slice(11, 16);

  const canProceed = mode === 'immediate' || (mode === 'scheduled' && scheduledDate && scheduledTime);

  const handleNext = () => {
    if (!canProceed) return;
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setSending(true);
    const scheduledAt = mode === 'scheduled' ? `${scheduledDate}T${scheduledTime}:00` : undefined;
    try {
      await onConfirm(campaign, mode, scheduledAt, selectedSegmentId !== 'all' ? selectedSegmentId : undefined);
      onClose();
    } finally {
      setSending(false);
    }
  };

  const formattedSchedule = mode === 'scheduled' && scheduledDate && scheduledTime
    ? new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
        year: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : null;

  return (
    <Modal open={open} onClose={sending ? undefined : onClose} title="Send Campaign" size="sm">
      {step === 'options' ? (
        <div className="p-6 space-y-5">
          {/* Campaign info */}
          <div className="rounded-lg bg-muted/50 border border-border px-4 py-3">
            <p className="text-xs text-muted-foreground mb-0.5">Campaign</p>
            <p className="text-sm font-semibold text-foreground truncate">{campaign.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{campaign.subject}</p>
          </div>

          {/* Brevo badge */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
            <Send size={12} className="text-blue-600 flex-shrink-0" />
            <p className="text-xs text-blue-700 font-medium">Delivered via <span className="font-bold">Brevo</span> — bounce tracking &amp; compliance enforced</p>
          </div>

          {/* Segment targeting */}
          <div>
            <button
              onClick={() => setSegmentExpanded((p) => !p)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border-2 border-border bg-card hover:border-primary/40 transition-all duration-150"
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: `${selectedSegment.color}18` }}>
                  <Filter size={12} style={{ color: selectedSegment.color }} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-semibold text-foreground">{selectedSegment.name}</p>
                  <p className="text-[10px] text-muted-foreground">{selectedSegment.subscriberCount.toLocaleString()} subscribers</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Target Segment</span>
                {segmentExpanded ? <ChevronUp size={13} className="text-muted-foreground" /> : <ChevronDown size={13} className="text-muted-foreground" />}
              </div>
            </button>
            {segmentExpanded && (
              <div className="mt-1.5 rounded-xl border border-border bg-card overflow-hidden shadow-sm">
                {BUILT_IN_SEGMENTS.map((seg) => (
                  <button
                    key={seg.id}
                    onClick={() => { setSelectedSegmentId(seg.id); setSegmentExpanded(false); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors text-left border-b border-border last:border-0 ${selectedSegmentId === seg.id ? 'bg-primary/5' : ''}`}
                  >
                    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${seg.color}18` }}>
                      <Users size={11} style={{ color: seg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">{seg.name}</p>
                      <p className="text-[10px] text-muted-foreground">{seg.subscriberCount.toLocaleString()} subscribers</p>
                    </div>
                    {selectedSegmentId === seg.id && <CheckCircle2 size={13} className="text-primary flex-shrink-0" />}
                  </button>
                ))}
                <div className="px-3 py-2 bg-muted/30 border-t border-border">
                  <a href="/segments" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
                    <Filter size={11} /> Manage segments
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Send mode options */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">When would you like to send?</p>

            <button
              onClick={() => setMode('immediate')}
              className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all duration-150 text-left ${
                mode === 'immediate' ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
              }`}
            >
              <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                mode === 'immediate' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                <Zap size={16} />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Send Immediately</p>
                <p className="text-xs text-muted-foreground mt-0.5">Start sending to all recipients right now</p>
              </div>
              {mode === 'immediate' && (
                <CheckCircle2 size={16} className="text-primary ml-auto mt-0.5 flex-shrink-0" />
              )}
            </button>

            <button
              onClick={() => setMode('scheduled')}
              className={`w-full flex items-start gap-3 p-4 rounded-xl border-2 transition-all duration-150 text-left ${
                mode === 'scheduled' ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
              }`}
            >
              <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                mode === 'scheduled' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                <Calendar size={16} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">Schedule for Later</p>
                <p className="text-xs text-muted-foreground mt-0.5">Pick a specific date and time to send</p>
              </div>
              {mode === 'scheduled' && (
                <CheckCircle2 size={16} className="text-primary ml-auto mt-0.5 flex-shrink-0" />
              )}
            </button>

            {mode === 'scheduled' && (
              <div className="flex gap-2 mt-1 pl-1">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">Date</label>
                  <input
                    type="date"
                    min={minDateStr}
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">Time</label>
                  <input
                    type="time"
                    min={scheduledDate === minDateStr ? minTimeStr : undefined}
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground outline-none focus:border-primary transition-colors"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-5">
          <div className="flex flex-col items-center text-center gap-3 py-2">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              {mode === 'immediate'
                ? <Zap size={24} className="text-primary" />
                : <Clock size={24} className="text-primary" />
              }
            </div>
            <div>
              <p className="text-base font-bold text-foreground">
                {mode === 'immediate' ? 'Send Now via Brevo?' : 'Schedule via Brevo?'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {mode === 'immediate' ?'Brevo will immediately begin delivering to all recipients with bounce tracking enabled.'
                  : `Brevo will send this campaign on ${formattedSchedule}.`
                }
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-muted/30 divide-y divide-border">
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground">Campaign</span>
              <span className="text-xs font-semibold text-foreground truncate max-w-[180px]">{campaign.name}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground">Sender</span>
              <span className="text-xs font-semibold text-foreground truncate max-w-[180px]">
                {campaign.fromEmail || <span className="text-red-500">No sender email set</span>}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground">Recipients</span>
              <span className="text-xs font-semibold text-foreground">{selectedSegment.subscriberCount.toLocaleString()} contacts</span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground">Segment</span>
              <span className="text-xs font-semibold flex items-center gap-1" style={{ color: selectedSegment.color }}>
                <Filter size={10} /> {selectedSegment.name}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground">Delivery</span>
              <span className="text-xs font-semibold text-blue-600 flex items-center gap-1">
                <Send size={10} /> Brevo API
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-xs text-muted-foreground">Send type</span>
              <span className="text-xs font-semibold text-foreground capitalize">
                {mode === 'immediate' ? 'Immediate' : 'Scheduled'}
              </span>
            </div>
            {mode === 'scheduled' && formattedSchedule && (
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-muted-foreground">Scheduled for</span>
                <span className="text-xs font-semibold text-foreground">{formattedSchedule}</span>
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
            <AlertCircle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">Once sent, this action cannot be undone. Brevo will enforce unsubscribe compliance automatically.</p>
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={() => setStep('options')} disabled={sending} className="btn-secondary flex-1">Back</button>
            <button
              onClick={handleConfirm}
              disabled={sending}
              className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {sending ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send size={14} />
                  {mode === 'immediate' ? 'Send via Brevo' : 'Schedule via Brevo'}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CampaignManagementClient() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<keyof Campaign>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [sendTarget, setSendTarget] = useState<Campaign | null>(null);
  // Map of local campaign id → bounce stats (fetched after send)
  const [bounceStatsMap, setBounceStatsMap] = useState<Record<string, BounceStats>>({});
  // Map of local campaign id → brevo campaign id (stored after successful send)
  const [brevoCampaignIdMap, setBrevoCampaignIdMap] = useState<Record<string, number>>({});
  // Real-time webhook stats (from Supabase campaign_stats table)
  const [webhookStatsMap, setWebhookStatsMap] = useState<Record<string, WebhookStats>>({});
  const [webhookStatsLoading, setWebhookStatsLoading] = useState<Record<string, boolean>>({});
  const [expandedWebhookRow, setExpandedWebhookRow] = useState<string | null>(null);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const data = await campaignService.getAll();
      setCampaigns(data);
    } catch (err: any) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return campaigns
      .filter((c) => {
        const matchSearch =
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.subject.toLowerCase().includes(search.toLowerCase()) ||
          c.listName.toLowerCase().includes(search.toLowerCase());
        const matchStatus = statusFilter === 'all' || c.status === statusFilter;
        return matchSearch && matchStatus;
      })
      .sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        const cmp = String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [campaigns, search, statusFilter, sortKey, sortDir]);

  const handleSort = (key: keyof Campaign) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.id)));
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    try {
      await Promise.all(ids.map((id) => campaignService.delete(id)));
      setCampaigns((prev) => prev.filter((c) => !selected.has(c.id)));
      toast.success(`${ids.length} campaigns deleted`);
      setSelected(new Set());
    } catch {
      toast.error('Failed to delete campaigns');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await campaignService.delete(id);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      toast.success('Campaign deleted');
      setDeleteConfirm(null);
    } catch {
      toast.error('Failed to delete campaign');
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const dup = await campaignService.duplicate(id);
      if (dup) {
        setCampaigns((prev) => [dup, ...prev]);
        toast.success('Campaign duplicated');
      }
    } catch {
      toast.error('Failed to duplicate campaign');
    }
  };

  const handleSaveCampaign = async (data: any) => {
    try {
      const tags = data.tags ? data.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [];
      const newCampaign = await campaignService.create({
        name: data.name,
        subject: data.subject,
        fromName: data.fromName,
        fromEmail: data.fromEmail,
        bodyHtml: data.bodyHtml || '',
        listName: data.mailingList,
        sendTiming: data.sendTiming,
        scheduledAt: data.scheduledAt || undefined,
        tags,
      });
      if (newCampaign) {
        setCampaigns((prev) => [newCampaign, ...prev]);
        setModalOpen(false);
        toast.success('Campaign created successfully');
      }
    } catch {
      toast.error('Failed to create campaign');
    }
  };

  // ── Brevo send handler ──────────────────────────────────────────────────────
  const handleSendConfirm = async (campaign: Campaign, mode: SendMode, scheduledAt?: string, segmentId?: string) => {
    try {
      const res = await fetch('/api/brevo/campaigns/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign: {
            id: campaign.id,
            name: campaign.name,
            subject: campaign.subject,
            fromName: campaign.fromName,
            fromEmail: campaign.fromEmail,
            bodyHtml: campaign.bodyHtml,
            listName: campaign.listName,
            // Pass existing Brevo campaign ID if already sent before
            brevoCampaignId: brevoCampaignIdMap[campaign.id],
          },
          mode,
          scheduledAt,
          segmentId,
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        toast.error(result.error || 'Failed to send campaign via Brevo');
        return;
      }

      // Store the Brevo campaign ID for future bounce lookups
      if (result.brevoCampaignId) {
        setBrevoCampaignIdMap((prev) => ({ ...prev, [campaign.id]: result.brevoCampaignId }));
      }

      // Update local campaign status
      if (mode === 'immediate') {
        setCampaigns((prev) =>
          prev.map((c) => c.id === campaign.id ? { ...c, status: 'sending' as CampaignStatus } : c)
        );
        toast.success(`"${campaign.name}" is now sending via Brevo`);

        // Fetch bounce stats after a short delay (Brevo needs a moment to process)
        if (result.brevoCampaignId) {
          setTimeout(async () => {
            try {
              const statsRes = await fetch(`/api/brevo/campaigns/bounces?campaignId=${result.brevoCampaignId}`);
              if (statsRes.ok) {
                const stats: BounceStats = await statsRes.json();
                setBounceStatsMap((prev) => ({ ...prev, [campaign.id]: stats }));
                if (stats.bounceStatus === 'critical') {
                  toast.error(`⚠️ High bounce rate detected: ${stats.bounceRate}% — check your list quality`);
                } else if (stats.bounceStatus === 'warning') {
                  toast.warning(`Bounce rate at ${stats.bounceRate}% — monitor closely`);
                }
              }
            } catch {}
          }, 3000);
        }
      } else {
        setCampaigns((prev) =>
          prev.map((c) => c.id === campaign.id
            ? { ...c, status: 'scheduled' as CampaignStatus, scheduledAt: scheduledAt || c.scheduledAt }
            : c
          )
        );
        const formattedDate = scheduledAt
          ? new Date(scheduledAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
          : '';
        toast.success(`"${campaign.name}" scheduled via Brevo for ${formattedDate}`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Network error while sending campaign');
    }
  };

  // ── Fetch bounce stats for a sent campaign on demand ──────────────────────
  const fetchBounceStats = async (campaign: Campaign) => {
    const brevoCampaignId = brevoCampaignIdMap[campaign.id];
    if (!brevoCampaignId) {
      toast.error('No Brevo campaign ID found. Send the campaign first.');
      return;
    }
    try {
      const res = await fetch(`/api/brevo/campaigns/bounces?campaignId=${brevoCampaignId}`);
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.error || 'Failed to fetch bounce stats');
        return;
      }
      const stats: BounceStats = await res.json();
      setBounceStatsMap((prev) => ({ ...prev, [campaign.id]: stats }));
      toast.success('Bounce stats refreshed');
    } catch {
      toast.error('Failed to fetch bounce stats');
    }
  };

  // ── Fetch real-time webhook stats for a campaign ──────────────────────────
  const fetchWebhookStats = async (campaignId: string) => {
    setWebhookStatsLoading((prev) => ({ ...prev, [campaignId]: true }));
    try {
      const res = await fetch(`/api/brevo/campaigns/stats?campaignId=${campaignId}`);
      if (res.ok) {
        const stats: WebhookStats = await res.json();
        setWebhookStatsMap((prev) => ({ ...prev, [campaignId]: stats }));
        if (stats.hasData && stats.bounce_status === 'critical') {
          toast.error(`⚠️ Critical bounce rate: ${stats.bounce_rate}% — check your list`);
        }
      }
    } catch {
      // silent
    } finally {
      setWebhookStatsLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  const toggleWebhookRow = (campaignId: string) => {
    if (expandedWebhookRow === campaignId) {
      setExpandedWebhookRow(null);
    } else {
      setExpandedWebhookRow(campaignId);
      if (!webhookStatsMap[campaignId]) {
        fetchWebhookStats(campaignId);
      }
    }
  };

  const SortIcon = ({ col }: { col: keyof Campaign }) => {
    if (sortKey !== col) return <ChevronUp size={12} className="text-border" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="text-primary" />
      : <ChevronDown size={12} className="text-primary" />;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === '—') return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  const canSend = (status: CampaignStatus) => status === 'draft' || status === 'scheduled';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Campaigns</h1>
          <p className="text-sm text-muted-foreground mt-1">{campaigns.length} total campaigns</p>
        </div>
        <button onClick={() => setModalOpen(true)} className="btn-primary">
          <Plus size={16} /> New Campaign
        </button>
      </div>

      {/* Brevo delivery banner */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200">
        <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Send size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-900">Brevo Email Delivery Connected</p>
          <p className="text-xs text-blue-700 mt-0.5">All campaign sends are delivered via Brevo API with automatic bounce tracking, unsubscribe compliance, and spam protection. Real-time webhook events update stats automatically.</p>
        </div>
        <a
          href="/brevo"
          className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
        >
          Brevo Dashboard <ExternalLink size={11} />
        </a>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {statusFilters.map((f) => (
            <button
              key={`filter-${f.value}`}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                statusFilter === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-primary'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card">
          <Search size={14} className="text-muted-foreground" />
          <input
            type="text"
            placeholder="Search campaigns…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-48"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="slide-up flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20">
          <CheckSquare size={16} className="text-primary" />
          <span className="text-sm font-semibold text-primary">{selected.size} selected</span>
          <div className="flex-1" />
          <button onClick={handleBulkDelete} className="btn-danger text-xs px-3 py-1.5">Delete Selected</button>
          <button onClick={() => setSelected(new Set())} className="btn-ghost text-xs px-3 py-1.5">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
              </svg>
              <p className="text-sm text-muted-foreground">Loading campaigns…</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selected.size === filtered.length && filtered.length > 0}
                      onChange={toggleAll}
                      className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                    />
                  </th>
                  {[
                    { key: 'name' as keyof Campaign, label: 'Campaign Name' },
                    { key: 'status' as keyof Campaign, label: 'Status' },
                    { key: 'listName' as keyof Campaign, label: 'List' },
                    { key: 'subject' as keyof Campaign, label: 'Subject' },
                    { key: 'sentCount' as keyof Campaign, label: 'Sent / Total' },
                    { key: 'openRate' as keyof Campaign, label: 'Open Rate' },
                    { key: 'ctr' as keyof Campaign, label: 'CTR' },
                    { key: 'scheduledAt' as keyof Campaign, label: 'Scheduled At' },
                    { key: 'tags' as keyof Campaign, label: 'Tags' },
                  ].map((col) => (
                    <th
                      key={`th-${col.key}`}
                      className="px-4 py-3 text-left metric-label cursor-pointer hover:text-foreground transition-colors duration-100"
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="flex items-center gap-1">
                        {col.label} <SortIcon col={col.key} />
                      </span>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left metric-label w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-16 text-muted-foreground text-sm">
                      No campaigns match your search
                    </td>
                  </tr>
                ) : (
                  filtered.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr
                        className={`border-b border-border transition-colors duration-100 hover:bg-muted/30 group ${
                          selected.has(c.id) ? 'bg-primary/5' : ''
                        } ${bounceStatsMap[c.id] || expandedWebhookRow === c.id ? '' : 'last:border-0'}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selected.has(c.id)}
                            onChange={() => toggleSelect(c.id)}
                            className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-foreground truncate max-w-[160px]">{c.name}</p>
                          <p className="text-xs text-muted-foreground">Created {formatDate(c.createdAt)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={statusVariant[c.status]}>
                            {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground max-w-[120px]">
                          <span className="truncate block">{c.listName}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px]">
                          <span className="truncate block" title={c.subject}>{c.subject}</span>
                        </td>
                        <td className="px-4 py-3 text-sm tabular-nums text-foreground whitespace-nowrap">
                          {c.sentCount > 0 ? `${c.sentCount.toLocaleString()} / ${c.totalCount.toLocaleString()}` : `— / ${c.totalCount.toLocaleString()}`}
                        </td>
                        <td className="px-4 py-3 text-sm tabular-nums font-semibold text-foreground">{c.openRate}</td>
                        <td className="px-4 py-3 text-sm tabular-nums font-semibold text-foreground">{c.ctr}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          {c.scheduledAt ? formatDate(c.scheduledAt) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 flex-wrap">
                            {c.tags?.slice(0, 2).map((tag) => (
                              <span key={`${c.id}-tag-${tag}`} className="badge bg-secondary text-secondary-foreground text-xs">
                                {tag}
                              </span>
                            ))}
                            {(c.tags?.length || 0) > 2 && (
                              <span className="text-xs text-muted-foreground">+{c.tags.length - 2}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {canSend(c.status) && (
                              <button
                                onClick={() => setSendTarget(c)}
                                title="Send via Brevo"
                                className="w-7 h-7 rounded flex items-center justify-center hover:bg-primary/10 transition-all duration-150"
                              >
                                <Send size={13} className="text-primary" />
                              </button>
                            )}
                            {(c.status === 'sending' || c.status === 'sent') && (
                              <button
                                onClick={() => toggleWebhookRow(c.id)}
                                title="View real-time webhook stats"
                                className={`w-7 h-7 rounded flex items-center justify-center hover:bg-muted transition-all duration-150 ${expandedWebhookRow === c.id ? 'bg-primary/10' : ''}`}
                              >
                                <Webhook size={13} className={expandedWebhookRow === c.id ? 'text-primary' : 'text-muted-foreground'} />
                              </button>
                            )}
                            {(c.status === 'sending' || c.status === 'sent') && brevoCampaignIdMap[c.id] && (
                              <button
                                onClick={() => fetchBounceStats(c)}
                                title="Refresh bounce stats"
                                className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted transition-all duration-150"
                              >
                                <TrendingDown size={13} className="text-muted-foreground" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDuplicate(c.id)}
                              title="Duplicate campaign"
                              className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted transition-all duration-150"
                            >
                              <Copy size={13} className="text-muted-foreground" />
                            </button>
                            <button
                              title="View analytics"
                              className="w-7 h-7 rounded flex items-center justify-center hover:bg-muted transition-all duration-150"
                            >
                              <BarChart2 size={13} className="text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(c.id)}
                              title="Delete campaign"
                              className="w-7 h-7 rounded flex items-center justify-center hover:bg-red-50 transition-all duration-150"
                            >
                              <Trash2 size={13} className="text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Real-time webhook stats row */}
                      {expandedWebhookRow === c.id && (
                        <tr className="border-b border-border last:border-0 bg-muted/10">
                          <td colSpan={11} className="px-6 py-3">
                            {webhookStatsMap[c.id] ? (
                              <WebhookStatsPanel
                                stats={webhookStatsMap[c.id]}
                                onRefresh={() => fetchWebhookStats(c.id)}
                                loading={!!webhookStatsLoading[c.id]}
                              />
                            ) : (
                              <div className="flex items-center gap-2 py-2">
                                <Loader2 size={14} className="animate-spin text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">Loading webhook stats…</span>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      {/* Bounce stats row */}
                      {bounceStatsMap[c.id] && (
                        <tr className="border-b border-border last:border-0 bg-muted/10">
                          <td colSpan={11} className="px-6 py-3">
                            <BounceStatsPanel stats={bounceStatsMap[c.id]} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">
              Showing {filtered.length} of {campaigns.length} campaigns
            </p>
            <div className="flex items-center gap-1">
              {[1, 2, 3].map((p) => (
                <button
                  key={`page-${p}`}
                  className={`w-7 h-7 rounded text-xs font-medium transition-all duration-150 ${
                    p === 1 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="New Campaign" size="xl">
        <CampaignForm
          onClose={() => setModalOpen(false)}
          onSave={handleSaveCampaign}
        />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Campaign" size="sm">
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this campaign? This action cannot be undone and all associated analytics will be lost.
          </p>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">Cancel</button>
            <button onClick={() => deleteConfirm && handleDelete(deleteConfirm)} className="btn-danger">
              Delete Campaign
            </button>
          </div>
        </div>
      </Modal>

      {/* Send Dialog */}
      <SendDialog
        campaign={sendTarget}
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        onConfirm={handleSendConfirm}
      />
    </div>
  );
}